import { toCamelCase, toEventName } from '../naming.js';

/**
 * Default lifecycle verbs every entity emits via base CRUD controller.
 * Catalogues automatically include these as `lifecycle: true`.
 */
export const DEFAULT_LIFECYCLE_VERBS = ['created', 'updated', 'deleted'];

/**
 * `defineEntity` — declarative, OCP-friendly catalog entry.
 *
 * Usage in `catalog/<entity>.events.js`:
 *
 *     import { defineEntity, lifecycle, custom } from './_helpers.js';
 *     export default defineEntity('risk', {
 *         reviewDue:      lifecycle(),                         // adds an extra lifecycle event
 *         statusChanged:  custom({ scope: 'state' }),
 *         treatmentAdded: custom({ scope: 'state' }),
 *     });
 *
 * Returns: { entity, events: { [verb|subResource.verb]: EventDef } }
 *
 * Each entry is normalised to:
 *     { name, entity, verb, subResource, lifecycle, scope, transports }
 */
export function defineEntity(entityRaw, customEvents = {}, options = {}) {
    const entity = toCamelCase(entityRaw);
    if (!entity) throw new Error(`defineEntity: invalid entity "${entityRaw}"`);
    const events = {};

    const includeDefaults = options.includeDefaultLifecycle !== false;
    if (includeDefaults) {
        for (const verb of DEFAULT_LIFECYCLE_VERBS) {
            events[verb] = normaliseEventDef(entity, verb, undefined, { lifecycle: true, scope: 'lifecycle' });
        }
        // base.controller batch delete + custom controllers emit `<entity>.bulk`
        events.bulk = normaliseEventDef(entity, 'bulk', undefined, { lifecycle: false, scope: 'state' });
    }

    for (const [key, def] of Object.entries(customEvents)) {
        const parts = key.split('.');
        let verb;
        let subResource = null;
        if (parts.length === 1) {
            verb = parts[0];
        } else if (parts.length === 2) {
            subResource = parts[0];
            verb = parts[1];
        } else {
            throw new Error(`defineEntity: invalid event key "${key}" — use 'verb' or 'subResource.verb'`);
        }
        events[key] = normaliseEventDef(entity, verb, subResource, def || {});
    }

    return { entity, events };
}

export function lifecycle(extra = {}) {
    return { lifecycle: true, scope: 'lifecycle', ...extra };
}

export function custom(extra = {}) {
    return { lifecycle: false, scope: extra.scope || 'state', ...extra };
}

export function ephemeral(extra = {}) {
    return { lifecycle: false, scope: 'ephemeral', ...extra };
}

function normaliseEventDef(entity, verbRaw, subResourceRaw, def) {
    const verb = toCamelCase(verbRaw);
    const subResource = subResourceRaw ? toCamelCase(subResourceRaw) : null;
    const name = subResource ? toEventName(entity, verb, subResource) : toEventName(entity, verb);
    return {
        name,
        entity,
        verb,
        subResource,
        lifecycle: !!def.lifecycle,
        scope: def.scope || (def.lifecycle ? 'lifecycle' : 'state'),
        transports: def.transports || null,
        payload: def.payload || null,
        description: def.description || null,
        deprecated: !!def.deprecated,
    };
}
